import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
    id: number;
    uuid: string;
    nom: string;
    prenom: string;
    email: string;
    role: string;
    telephone?: string;
    photo_url?: string;
    zone_id?: number;
    permissions?: string[];
    scopes?: Record<string, string>;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginOtp: (email: string) => Promise<any>;
    verifyOtp: (userId: number, otp: string) => Promise<void>;
    logout: () => void;
    isManager: () => boolean;
    isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('sp_token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            api.get('/auth/me')
                .then(res => setUser(res.data))
                .catch(() => { localStorage.removeItem('sp_token'); setToken(null); })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [token]);

    const login = async (email: string, password: string) => {
        const res = await api.post('/auth/login', { email, password });
        localStorage.setItem('sp_token', res.data.token);
        setToken(res.data.token);
        setUser({
            ...res.data.user,
            permissions: res.data.permissions || [],
            scopes: res.data.scopes || {}
        });
    };

    const loginOtp = async (email: string) => {
        const res = await api.post('/auth/login-otp', { email });
        return res.data;
    };

    const verifyOtp = async (userId: number, otp: string) => {
        const res = await api.post('/auth/verify-otp', { userId, otp });
        localStorage.setItem('sp_token', res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
    };

    const logout = () => {
        localStorage.removeItem('sp_token');
        setToken(null);
        setUser(null);
    };

    const isManager = () => ['superadmin', 'admin', 'manager', 'supervisor'].includes(user?.role || '');
    const isAdmin = () => ['superadmin', 'admin'].includes(user?.role || '');

    return (
        <AuthContext.Provider value={{ user, token, loading, login, loginOtp, verifyOtp, logout, isManager, isAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
